import type { TileUnit, TileWarningCode } from "@kundeninfo/api-client";
import { formatBetrag, formatDatum } from "../../format.js";
import { CALENDAR_TEXT } from "../kalender/calendarTexts.js";
import { HIERARCHY_TEXT } from "../tickets/hierarchyTexts.js";

/**
 * ANWENDERTEXTE DER COCKPIT-OBERFLÄCHE (K33). Kein Baustein schreibt einen Text selbst. Katalog, Reihen, Zahlen und `detail`
 * kommen vom Server (`COCKPIT_TEXT` im api-Paket), hier stehen Leiste, Zeitraum, Dialoge, Einheiten, Popup-Muster, Warnungen und
 * die Sätze der Listenzeilen.
 */

const NUMBER = new Intl.NumberFormat("de-DE");
const ONE_DECIMAL_AT_MOST = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 });
const ONE_DECIMAL = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const count = (n: number, one: string, many: string): string => `${NUMBER.format(n)} ${n === 1 ? one : many}`;

const COMPARE_NAMES = { PREVIOUS_PERIOD: "Vorperiode", PREVIOUS_YEAR: "Vorjahr" } as const;
type CompareName = keyof typeof COMPARE_NAMES;
/** Die Hauswährung, falls eine Betragszahl ohne `currency` ankommt (`COCKPIT_CURRENCY` der Domäne). */
const HOME_CURRENCY = "EUR";

/** Ein Wert mit Einheit: Betrag aus `formatBetrag` (glatte Beträge ohne „,00“), Minuten als Stunden mit einer Nachkommastelle. */
function valueText(value: number, unit: TileUnit | "PERCENT", currency: string | null): string {
  switch (unit) {
    case "MONEY": return formatBetrag(value, currency ?? HOME_CURRENCY).replace(",00 ", " ");
    case "MINUTES": return `${ONE_DECIMAL.format(value / 60)} h`;
    case "PERCENT": return `${ONE_DECIMAL_AT_MOST.format(value)} %`;
    case "COUNT": return NUMBER.format(value);
  }
}

/** Die Wertachse kurz: „80 T€“, „1,2 Mio. €“, „1,5 h“. Reihen tragen immer die Hauswährung (K14), Minuten stehen dort schon als Stunden. */
function axisValue(value: number, unit: TileUnit): string {
  if (unit === "MINUTES") return `${ONE_DECIMAL_AT_MOST.format(value)} h`;
  if (unit === "COUNT") return NUMBER.format(value);
  const size = Math.abs(value);
  if (size >= 1e6) return `${ONE_DECIMAL_AT_MOST.format(value / 1e6)} Mio. €`;
  return size >= 1e3 ? `${ONE_DECIMAL_AT_MOST.format(value / 1e3)} T€` : `${ONE_DECIMAL_AT_MOST.format(value)} €`;
}

type ActivityParams = Readonly<Record<string, string | number>>;
type Actor = string | null;
/** „Anna hat kommentiert“; ohne Person (System) `alone`. */
const did = (actor: Actor, what: string, alone: string): string => (actor === null ? alone : `${actor} hat ${what}`);
/** „Anna: Eigentümer Ben“; ohne Person nur die Angabe. */
const said = (actor: Actor, what: string): string => (actor === null ? what : `${actor}: ${what}`);
const orUnknown = (value: string | number | undefined): string => String(value ?? "?");
/** Wie im Verlauf des Tickets: Person mit Name, Team mit Kürzel, sonst „System“. */
function ownerName({ toKind, toName }: ActivityParams): string {
  if (toKind === "TEAM") return `Team ${orUnknown(toName)}`;
  return toKind === "USER" ? orUnknown(toName) : HIERARCHY_TEXT.system;
}
/** Ein kaputter Tag wirft nicht, er steht wörtlich da (vorwärtsverträglich wie `textKey`). */
function dueText(to: string | number | undefined): string {
  if (to === undefined) return "entfernt";
  return Number.isNaN(Date.parse(String(to))) ? String(to) : formatDatum(String(to));
}
/** Satzbauer der Listenzeilen je `textKey` (`recentActivity`, K29): Der Server liefert Daten, die Sätze entstehen nur hier. */
const ACTIVITY: Readonly<Record<string, (params: ActivityParams, actor: Actor) => string>> = {
  ticketCreated: (_p, actor) => did(actor, "angelegt", "Angelegt"),
  columnChanged: ({ from, to }, actor) => said(actor, `${from === undefined ? "" : `${from} `}→ ${orUnknown(to)}`),
  ownerChanged: (p, actor) => said(actor, `Eigentümer ${ownerName(p)}`),
  dueDateChanged: ({ to }, actor) => said(actor, `Fällig bis ${dueText(to)}`),
  ticketCommented: (_p, actor) => did(actor, "kommentiert", "Kommentiert"),
  noteCreated: (_p, actor) => did(actor, "eine Notiz angelegt", "Notiz angelegt"),
  fileUploaded: ({ name }, actor) => did(actor, `„${orUnknown(name)}“ hochgeladen`, `„${orUnknown(name)}“ hochgeladen`),
};

export const COCKPIT_TEXT_WEB = {
  // ── Bereich ───────────────────────────────────────────────────────────────────────────────────────────────────
  area: "Cockpit",
  backToArea: "Zurück zum Cockpit",
  loading: "Cockpit wird geladen …",
  catalogFailed: "Das Cockpit konnte nicht geladen werden.",
  tileFailed: "Konnte nicht geladen werden.",
  reload: "Erneut laden",

  // ── Werkzeugleiste ────────────────────────────────────────────────────────────────────────────────────────────
  toolbar: {
    name: "Werkzeuge des Cockpits",
    company: "Unternehmen",
    allCompanies: "Alle",
    compare: "Vergleich",
    compareModes: { NONE: "aus", ...COMPARE_NAMES },
    alignment: "Ausrichtung",
    alignments: { CALENDAR: "kalendergleich", WEEKDAY: "wochentaggleich" },
    customize: "Kacheln anpassen",
    customizeDone: "Fertig",
    /** Name der Gruppe aus Hinweis, „Kacheln wählen …“ und Zahnrad. */
    tiles: "Kacheln",
    pickTiles: "Kacheln wählen …",
    newTiles: (n: number): string => count(n, "neue Kachel verfügbar", "neue Kacheln verfügbar"),
  },

  // ── Zeitraum ──────────────────────────────────────────────────────────────────────────────────────────────────
  period: {
    name: "Zeitraum",
    kinds: { MONTH: "Monat", QUARTER: "Quartal", YEAR: "Jahr", FREE: "frei" },
    /** Am Telefon der sichtbare Kurztext der Segmente (`data-kurz`, me-design C8); Name und Sofort-Popup bleiben `kinds`. */
    kindsShort: { MONTH: "M", QUARTER: "Q", YEAR: "J", FREE: "frei" },
    today: "Heute",
    earlierGlyph: "◂",
    earlier: "Früherer Zeitraum",
    laterGlyph: "▸",
    later: "Späterer Zeitraum",
    months: CALENDAR_TEXT.months,
    monthsShort: ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"],
    monthName: (month: string, year: string): string => `${month} ${year}`,
    quarterName: (quarter: number, year: string): string => `Q${quarter} ${year}`,
    /** „01.10.–31.10.2026“: Die Tage kommen fertig formatiert aus `cockpitPeriod.ts`. */
    range: (from: string, to: string): string => `${from}–${to}`,
    /** „Oktober 2026 · 01.10.–31.10.2026“ */
    window: (name: string, range: string): string => `${name} · ${range}`,
    /** „frei · 05.10.–18.10.2026 (14 Tage)“ */
    freeWindow: (range: string, days: number): string => `frei · ${range} (${count(days, "Tag", "Tage")})`,
    /** „Vergleich 27.08.–26.09.2026 · wochentaggleich“ */
    compare: (range: string, alignment: string): string => `Vergleich ${range} · ${alignment}`,
    /** Rückweg nach dem Hineinklicken: „‹ 2026“ mit dem kurzen Fenstertext des Rückwegs. */
    back: (text: string): string => `‹ ${text}`,
    backHint: "Zurück zum vorigen Zeitraum (Esc)",
    /** Im Modus Anpassen gehört Esc der Wand. */
    backHintPlain: "Zurück zum vorigen Zeitraum",
  },

  // ── Dialog „frei“ ─────────────────────────────────────────────────────────────────────────────────────────────
  freeDialog: {
    title: "Zeitraum frei wählen",
    from: "von",
    to: "bis",
    /** Mit geschütztem Leerzeichen: Die Zahl bricht nicht um. */
    rangeRule: "1 bis 1\u00A0096 Tage",
    apply: "Übernehmen",
    cancel: "Abbrechen",
  },

  // ── Anordnung ─────────────────────────────────────────────────────────────────────────────────────────────────
  layout: {
    saveFailed: "Anordnung konnte nicht gespeichert werden – sie gilt bis zum Neuladen.",
    resetFailed: "Anordnung konnte nicht zurückgesetzt werden – bitte noch einmal versuchen.",
    allHidden: "Alle Kacheln ausgeblendet",
  },

  // ── Kachelwahl ────────────────────────────────────────────────────────────────────────────────────────────────
  picker: {
    title: "Kacheln wählen",
    show: "zeigen",
    /** Der Name des Schalters: Sichtbar steht je Kachel nur „zeigen“. */
    showTile: (title: string): string => `${title} zeigen`,
    isNew: "neu",
    size: "Größe",
    sizeOf: (title: string): string => `Größe ${title}`,
    sizes: { "1x1": "1×1", "2x1": "2×1", "2x2": "2×2" },
    display: "Darstellung",
    displayOf: (title: string): string => `Darstellung ${title}`,
    displays: { COUNT: "Zahlen", SERIES: "Verlauf", STACKED: "gestapelt", LIST: "Liste" },
    save: "Speichern",
    saveFailed: "Die Kachelwahl konnte nicht gespeichert werden.",
    cancel: "Abbrechen",
    discardQuestion: "Änderungen verwerfen?",
    discardText: "Die geänderte Kachelwahl wird nicht gespeichert.",
    discardConfirm: "Verwerfen",
    discardKeep: "Weiter bearbeiten",
  },

  // ── Zahlen und Diagramme ──────────────────────────────────────────────────────────────────────────────────────
  valueText,
  now: "jetzt",
  axis: {
    week: (week: number): string => `KW ${week}`,
    value: axisValue,
  },
  popup: {
    noValue: "–",
    sum: "Summe",
    compareModes: COMPARE_NAMES,
    /** „KW 14 · 30.03.–05.04.2026“, angeschnitten mit „Teilwoche“ davor. */
    week: (week: number, range: string, partial: boolean): string => `${partial ? "Teilwoche " : ""}KW ${week} · ${range}`,
    /** `month` 1–12: „April 2026“, angeschnitten „Teilmonat 15.04.–30.04.2026“. */
    month: (month: number, year: string, range: string, partial: boolean): string =>
      (partial ? `Teilmonat ${range}` : `${CALENDAR_TEXT.months[month - 1]} ${year}`),
    line: (label: string, value: string): string => `${label}: ${value}`,
    /** „Vorjahr April 2025“; ohne Gegenstück im Vergleich (er ist kürzer als die Basis) nur „Vorjahr“. */
    compareSection: (mode: CompareName, section: string | null): string =>
      (section === null ? COMPARE_NAMES[mode] : `${COMPARE_NAMES[mode]} ${section}`),
    /** „5 · 5.000 €“: Anzahl und Betrag einer Reihe mit `amounts`. */
    withAmount: (value: string, amount: string): string => `${value} · ${amount}`,
    /** Das Vorzeichen bleibt (Zustand nie nur über Farbe); `text` ist der Betrag ohne Vorzeichen. */
    signed: (value: number, text: string): string => `${value > 0 ? "+" : value < 0 ? "−" : ""}${text}`,
    /** „Δ +14.300 € (+13 %)“; Prozent nur bei einem Vergleichswert ungleich 0. */
    delta: (value: string, percent: string | null): string => (percent === null ? `Δ ${value}` : `Δ ${value} (${percent})`),
  },

  // ── Kachelrahmen ──────────────────────────────────────────────────────────────────────────────────────────────
  tile: {
    /** Statt „Keine Einträge“, solange die erste Antwort fehlt. */
    loading: "Lädt …",
    /** Der Name der Zeichenfläche, solange die erste Antwort fehlt. */
    loadingName: (title: string): string => `${title}: wird geladen`,
    /** Im Diagramm oder unter den Zahlen ohne einen einzigen Wert. */
    noValues: "Keine Werte im Zeitraum.",
    /** Statt `noValues`, wenn die Antwort `MISSING_SNAPSHOTS` meldet: vor dem ersten Lauf oder für einen Zeitraum ohne jeden Stichtag. */
    noSnapshot: "Kein Schnappschuss aus Salesforce im Zeitraum. Schnappschüsse entstehen jede Nacht ab dem ersten Lauf.",
    /** Eine Liste ohne Zeilen; in Stufe 1 ist „Letzte Aktionen“ die einzige Kachel in dieser Darstellung. */
    noRows: "Keine Aktionen in den letzten 30 Tagen.",
    /** „Vorjahr 49“: die Vergleichszeile unter einer Zahl. */
    compareLine: (mode: CompareName, value: string): string => `${COMPARE_NAMES[mode]} ${value}`,
    /** „12.400,00 € netto · Vorjahr 49“: die Stücke eines Unter-Texts oder der Warnungen. */
    parts: (parts: readonly string[]): string => parts.join(" · "),
    /** „ME + BS“: der eine Legendeneintrag, wenn das Diagramm die Summe zeichnet. */
    sumOf: (labels: readonly string[]): string => labels.join(" + "),
  },

  // ── Warnungen unter der Zeichenfläche ─────────────────────────────────────────────────────────────────────────
  // Je Wert von `TileWarningCode` ein Satz: Fehlt einer, meldet es die Typprüfung (PF-9).
  warnings: {
    PARTIAL_HISTORY: (n: number): string => `Verlauf vor dem 02.11.2026 teilweise geschätzt (${count(n, "Ticket", "Tickets")})`,
    OTHER_CURRENCIES: (n: number): string => `Belege in anderen Währungen nicht enthalten (${NUMBER.format(n)})`,
    TRUNCATED: (n: number): string => `Gekappt: ${NUMBER.format(n)} Einträge, nicht alle gezählt`,
    UNDATED: (n: number): string => `${count(n, "entschiedenes Angebot", "entschiedene Angebote")} ohne Datum nicht enthalten`,
    MISSING_SNAPSHOTS: (n: number): string => `${count(n, "Tag", "Tage")} ohne Schnappschuss aus Salesforce, dort bleibt die Treppe leer`,
  } satisfies Readonly<Record<TileWarningCode, (n: number) => string>>,

  // ── Barrierefreiheit ──────────────────────────────────────────────────────────────────────────────────────────
  a11y: {
    /** „Umsatz netto 2026: 812.400 €, Vorjahr 765.000 €“ */
    summary: (title: string, period: string, value: string, comparison: string | null): string =>
      `${title} ${period}: ${value}${comparison === null ? "" : `, ${comparison}`}`,
    comparison: (label: string, value: string): string => `${label} ${value}`,
    tableHead: { bucket: "Abschnitt", sum: "Summe", compare: "Vergleich", delta: "Δ" },
  },

  // ── Listenzeilen ──────────────────────────────────────────────────────────────────────────────────────────────
  /** Der Satz einer Zeile aus `textKey`; ein unbekannter Schlüssel ergibt die neutrale Zeile (vorwärtsverträglich). */
  activity: (textKey: string, params: ActivityParams, actor: string | null): string =>
    (Object.hasOwn(ACTIVITY, textKey) ? ACTIVITY[textKey]!(params, actor)
      : actor === null ? "Etwas wurde geändert" : `${actor} hat etwas geändert`),
} as const;
